import { useEffect, useState, type ReactElement } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { Check, ChevronLeft, Trash2 } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import AiAssistComposer from '@/design/AiAssistComposer';
import DrillBreadcrumb from '@/design/DrillBreadcrumb';
import { ClassificationPageViewModel } from './ClassificationPageViewModel';
import { categoryAssistExamples, categoryFromParam, categoryNoun, surfaceForCategory, STATUS_OPTIONS } from './ClassificationOptions';
import type { ClassificationStatus } from './ClassificationService';
import ClassificationListRail from './ClassificationListRail';
import ClassificationDefinitionCard from './ClassificationDefinitionCard';
import ClassificationInstructionsCard from './ClassificationInstructionsCard';
import ClassificationAssistModal from './ClassificationAssistModal';

interface ClassificationPageProps {
    /** Overrides the URL `:category` param so the builder can be embedded outside the classification route (e.g. Settings → Policies). */
    category?: string;
    /** Hides the surface breadcrumb when the page is hosted inside another shell. */
    embedded?: boolean;
}

export default function ClassificationPage({ category: categoryProp, embedded = false }: ClassificationPageProps = {}): ReactElement {
    const { category: categoryParam } = useParams<{ category: string }>();
    const [searchParams] = useSearchParams();
    const draftPrompt = searchParams.get('draft') ?? undefined;
    const category = categoryFromParam(categoryProp ?? categoryParam);
    const vm = useViewModel(ClassificationPageViewModel);
    const isWide = useBreakpointUp('sm');
    const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

    useEffect(() => {
        if (category !== undefined) void vm.initialize(category, draftPrompt);
    }, [vm, category, draftPrompt]);

    if (category === undefined) return <Navigate to="/insights" replace />;

    const noun = categoryNoun(category);
    const surfaceId = surfaceForCategory(category);
    const breadcrumbItems =
        surfaceId !== undefined ?
            [{ label: surfaceId.charAt(0).toUpperCase() + surfaceId.slice(1), to: `/${surfaceId}` }, { label: noun.plural }]
        :   [{ label: noun.plural }];

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {!embedded && (
                <Box style={{ flexShrink: 0 }}>
                    <DrillBreadcrumb items={breadcrumbItems} />
                </Box>
            )}
            <Box style={{ flexShrink: 0 }}>
                <PageHeaderComponent
                    title={`${category} ${noun.plural}`}
                    subtitle="Describe a behavior to classify, write the instructions, and choose how the result is expressed."
                    rightToolbar={
                        <Group gap="sm" wrap="nowrap">
                            <SaveIndicator saving={vm.saveStatus === 'saving'} saved={vm.saveStatus === 'saved'} error={vm.errorMessage} />
                            <Select
                                size="xs"
                                w={130}
                                aria-label="Status"
                                allowDeselect={false}
                                data={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
                                value={vm.draft.status}
                                onChange={(v) => v && vm.setStatus(v as ClassificationStatus)}
                            />
                            {!vm.isNew && (
                                <Button
                                    variant="default"
                                    size="xs"
                                    leftSection={<Trash2 size={14} />}
                                    onClick={() => vm.selectedId && void vm.remove(vm.selectedId)}
                                >
                                    Delete
                                </Button>
                            )}
                            <Button size="xs" loading={vm.saveStatus === 'saving'} onClick={() => void vm.save()}>
                                Save
                            </Button>
                        </Group>
                    }
                />
            </Box>

            {(isWide || mobileView === 'detail') && (
                <Box mb="md">
                    <AiAssistComposer
                        subtitle={`Describe the ${noun.plural.toLowerCase()} you want — I'll propose changes you can review and apply.`}
                        scopeLabel={vm.lockedCategory}
                        collapsible
                        defaultCollapsed={draftPrompt === undefined}
                        promptText={vm.assistPrompt}
                        placeholder="Take a look at my policies and fill in any that are missing."
                        generating={vm.assistBusy}
                        error={vm.errorMessage}
                        examples={categoryAssistExamples(category)}
                        canGenerate={vm.assistPrompt.trim().length > 0 && !vm.assistBusy}
                        onPromptChange={(v) => vm.setAssistPrompt(v)}
                        onGenerate={() => void vm.generateSuggestions()}
                    />
                </Box>
            )}

            <Box
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: isWide ? 'row' : 'column',
                    border: '1px solid var(--bm-color-border)',
                    borderRadius: 'var(--bm-radius)',
                    overflow: 'hidden',
                    marginBottom: 16,
                }}
            >
                {(isWide || mobileView === 'list') && (
                    <ClassificationListRail
                        classifications={vm.filteredClassifications}
                        selectedId={vm.selectedId}
                        searchQuery={vm.searchQuery}
                        noun={noun}
                        onSearch={(v) => vm.setSearchQuery(v)}
                        onSelect={(id) => {
                            vm.select(id);
                            setMobileView('detail');
                        }}
                        onNew={() => {
                            vm.newClassification();
                            setMobileView('detail');
                        }}
                        stacked={!isWide}
                    />
                )}

                {(isWide || mobileView === 'detail') && (
                <Box style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                    {!isWide && (
                        <Box style={{ borderBottom: '1px solid var(--bm-color-border)' }} p="xs">
                            <Button variant="subtle" size="xs" color="gray" leftSection={<ChevronLeft size={14} />} onClick={() => setMobileView('list')}>
                                {noun.plural}
                            </Button>
                        </Box>
                    )}
                    {vm.loading ?
                        <Group justify="center" p="xl">
                            <Loader size="sm" />
                        </Group>
                    :   <Stack gap="lg" p="xl">
                            <ClassificationDefinitionCard
                                name={vm.draft.name}
                                description={vm.draft.description}
                                noun={noun}
                                onNameChange={(v) => vm.setName(v)}
                                onDescriptionChange={(v) => vm.setDescription(v)}
                            />
                            <ClassificationInstructionsCard
                                instructions={vm.draft.instructions}
                                outputType={vm.draft.outputType}
                                outputConfig={vm.draft.outputConfig}
                                noun={noun}
                                onInstructionsChange={(v) => vm.setInstructions(v)}
                                onOutputTypeChange={(v) => vm.setOutputType(v)}
                                onOutputConfigChange={(patch) => vm.setOutputConfig(patch)}
                            />
                        </Stack>
                    }
                </Box>
                )}
            </Box>

            <ClassificationAssistModal vm={vm} noun={noun} />
        </Box>
    );
}

interface SaveIndicatorProps {
    saving: boolean;
    saved: boolean;
    error: string | null;
}

function SaveIndicator({ saving, saved, error }: SaveIndicatorProps): ReactElement | null {
    if (error) {
        return (
            <Text size="xs" c="red">
                {error}
            </Text>
        );
    }
    if (saving) {
        return (
            <Group gap={6} wrap="nowrap">
                <Loader size={13} />
                <Text size="xs" c="dimmed">
                    Saving…
                </Text>
            </Group>
        );
    }
    if (saved) {
        return (
            <Group gap={6} wrap="nowrap">
                <Check size={13} style={{ color: 'var(--bm-color-text-dimmed)' }} />
                <Text size="xs" c="dimmed">
                    Saved
                </Text>
            </Group>
        );
    }
    return null;
}
